import React, { useEffect, useState } from "react";
import { Box, FormControl, SxProps, TextField } from "@mui/material";
import { UISelect } from "./UISelect";
import { UILabel } from "./UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { UIButton } from "./UIButton";
import { IBuildingBlockBaseControl } from "./BuildingBlockInterfaces";

export interface UITextListInterface extends IBuildingBlockBaseControl {
    placeholder?: string;
    autoFocus?: boolean;
    validValues?: string[];
    onChange?: (val: string[]) => void;
    value?: string[];
    inputSx?: SxProps;
}

export function UITextList(props: UITextListInterface) {
    const [values, setValues] = useState<string[]>(props.value || []);

    useEffect(() => {
        setValues(props.value || []);
    }, [props.value]);

    const update = (vals: string[]) => {
        if (props.onChange) props.onChange(vals);
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues([...event.target.value.split("\n")]);
    };

    const onSelect = (newValue: string, index: number) => {
        values[index] = newValue;
        update(values);
    };

    const onBlur = () => {
        update(values);
    };

    const add = () => {
        values.push("");
        update(values);
    };

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
        >
            {props.validValues && props.validValues.length > 0 ? (
                <>
                    <UILabel>{props.label}</UILabel>
                    <Box
                        minHeight={"35px"}
                        display={"flex"}
                        flexDirection={"column"}
                        padding={"10px"}
                        border={
                            "1px solid " +
                            (props.error ? UIStyleConstants.RED_COLOR : UIStyleConstants.GRAY_LIGHTER_COLOR)
                        }
                        borderRadius="4pxpx"
                    >
                        {(values || []).map((val: string, idx: number) => (
                            <UISelect
                                sx={{ paddingBottom: "5px", ...props.inputSx }}
                                value={val}
                                key={idx}
                                options={props.validValues!}
                                onChange={(event) => {
                                    onSelect(event!, idx);
                                }}
                            ></UISelect>
                        ))}
                        {(!values || values.length === 0) && <UILabel>Click Add</UILabel>}
                    </Box>
                    {(props.hint || props.error) && (
                        <UILabel variant={props.error ? "error_hint" : "hint"}>{props.error ?? props.hint}</UILabel>
                    )}
                    <Box marginTop={"5px"}>
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"end"}
                        >
                            <UIButton onClick={add}>Add</UIButton>
                        </Box>
                    </Box>
                </>
            ) : (
                <FormControl sx={props.sx}>
                    <TextField
                        label={props.label}
                        variant="outlined"
                        value={(values || []).join("\n")}
                        onChange={onChange}
                        onBlur={onBlur}
                        multiline
                        error={Boolean(props.error)}
                        minRows={5}
                        fullWidth
                        InputProps={{
                            classes: { root: "TextList-TextBox" },
                        }}
                        data-testid="TextList-TextBox"
                        placeholder={props.placeholder}
                        sx={props.inputSx}
                    />
                    {(props.hint || props.error) && (
                        <UILabel variant={props.error ? "error_hint" : "hint"}>{props.error ?? props.hint}</UILabel>
                    )}
                </FormControl>
            )}
        </Box>
    );
}
